import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { RouterLink } from "~/components/router-link";
import { Scrollbar } from "~/components/scrollbar";
import { usePathname } from "~/hooks/use-pathname";
import { paths } from "~/paths";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import type { NavColor } from "~/types/settings";
import { TenantSwitch } from "../tenant-switch";
import { SideNavSection } from "./side-nav-section";
import type { FC } from "react";
import type { Section } from "../config";
import { SettingsDrawer } from "~/components/settings/settings-drawer";
import { useSettings } from "~/hooks/use-settings";
import { SettingsButton } from "~/components/settings/settings-button";
import { Chip, SwipeableDrawer } from "@mui/material";
const SIDE_NAV_WIDTH = 280;
const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();
  return useMemo((): Record<string, string> => {
    switch (color) {
      case "blend-in":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": theme.palette.neutral[400],
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": theme.palette.neutral[500],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": theme.palette.neutral[700],
            "--nav-scrollbar-color": theme.palette.neutral[400]
          };
        } else {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.neutral[100],
            "--nav-logo-border": theme.palette.neutral[100],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": theme.palette.text.secondary,
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": theme.palette.neutral[400],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": theme.palette.neutral[400],
            "--nav-scrollbar-color": theme.palette.neutral[900]
          };
        }
      case "discrete":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[900],
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": theme.palette.neutral[400],
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": theme.palette.neutral[500],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": theme.palette.neutral[700],
            "--nav-scrollbar-color": theme.palette.neutral[400]
          };
        } else {
          return {
            "--nav-bg": theme.palette.neutral[50],
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.divider,
            "--nav-logo-border": theme.palette.neutral[200],
            "--nav-section-title-color": theme.palette.neutral[500],
            "--nav-item-color": theme.palette.neutral[500],
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": theme.palette.neutral[400],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": theme.palette.neutral[400],
            "--nav-scrollbar-color": theme.palette.neutral[900]
          };
        }
      case "evident":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[800],
            "--nav-color": theme.palette.common.white,
            "--nav-border-color": "transparent",
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": theme.palette.neutral[400],
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.common.white,
            "--nav-item-disabled-color": theme.palette.neutral[500],
            "--nav-item-icon-color": theme.palette.neutral[400],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[500],
            "--nav-item-chevron-color": theme.palette.neutral[600],
            "--nav-scrollbar-color": theme.palette.neutral[400]
          };
        } else {
          return {
            "--nav-bg": theme.palette.neutral[800],
            "--nav-color": theme.palette.common.white,
            "--nav-border-color": "transparent",
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": theme.palette.neutral[400],
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.common.white,
            "--nav-item-disabled-color": theme.palette.neutral[500],
            "--nav-item-icon-color": theme.palette.neutral[400],
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[500],
            "--nav-item-chevron-color": theme.palette.neutral[600],
            "--nav-scrollbar-color": theme.palette.neutral[400]
          };
        }
      default:
        return {};
    }
  }, [theme, color]);
};
interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
}
export const SideNav: FC<SideNavProps> = props => {
  const {
    color = "evident",
    sections = []
  } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);
  const settings = useSettings();
  const {
    data
  } = useGetCurrentCompanyCache();
  return <>
			<SwipeableDrawer anchor="left"
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onOpen={() => {}} open={settings.isNavDrawerOpen} onClose={settings.handleCloseNavDrawer} PaperProps={{
      sx: {
        ...cssVars,
        backgroundColor: "var(--nav-bg)",
        borderRightColor: "var(--nav-border-color)",
        borderRightStyle: "solid",
        borderRightWidth: 1,
        color: "var(--nav-color)",
        width: SIDE_NAV_WIDTH
      }
    }} data-sentry-element="SwipeableDrawer" data-sentry-source-file="side-nav.tsx">
				<Scrollbar sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%"
        },
        "& .simplebar-scrollbar:before": {
          background: "var(--nav-scrollbar-color)"
        }
      }} data-sentry-element="Scrollbar" data-sentry-source-file="side-nav.tsx">
					<Stack sx={{
          height: "100%"
        }} data-sentry-element="Stack" data-sentry-source-file="side-nav.tsx">
						<Stack alignItems="center" spacing={2} sx={{
            p: 3
          }} data-sentry-element="Stack" data-sentry-source-file="side-nav.tsx">
							<Box component={RouterLink} href={paths.index} data-sentry-element="Box" data-sentry-source-file="side-nav.tsx">
								{data ? <img style={{
                width: "60px",
                maxWidth: "100vw",
                maxHeight: "60px",
                aspectRatio: "1/1",
                objectFit: "cover",
                objectPosition: "center",
                borderRadius: 8
              }} src={data.logo ?? undefined} alt="Company Logo" /> : <Box sx={{
                borderColor: "var(--nav-logo-border)",
                borderRadius: 1,
                borderStyle: "solid",
                borderWidth: 1,
                display: "flex",
                height: 60,
                p: "4px",
                width: 60
              }}>
										<img src="/favicon.png" width="60px" />
									</Box>}
							</Box>
							<TenantSwitch sx={{
              flexGrow: 1
            }} data-sentry-element="TenantSwitch" data-sentry-source-file="side-nav.tsx" />
						</Stack>
						<Stack component="nav" spacing={2} sx={{
            flexGrow: 1,
            px: 2
          }} data-sentry-element="Stack" data-sentry-source-file="side-nav.tsx">
							{sections.map((section, index) => <SideNavSection items={section.items} key={index} pathname={pathname} subheader={section.subheader} />)}
						</Stack>
						<Stack alignItems="center" justifyContent="space-between" flexDirection="row" sx={{
            flexGrow: 1,
            px: 2
          }} data-sentry-element="Stack" data-sentry-source-file="side-nav.tsx">
							<SettingsButton onClick={settings.handleOpenSettingDrawer} data-sentry-element="SettingsButton" data-sentry-source-file="side-nav.tsx" />
							<SettingsDrawer canReset={settings.isCustom} onClose={settings.handleCloseSettingDrawer} onReset={settings.handleReset} onUpdate={settings.handleUpdate} open={settings.isSettingDrawerOpen} values={{
              colorPreset: settings.colorPreset,
              contrast: settings.contrast,
              direction: settings.direction,
              paletteMode: settings.paletteMode,
              responsiveFontSizes: settings.responsiveFontSizes,
              stretch: settings.stretch,
              layout: settings.layout,
              navColor: settings.navColor
            }} data-sentry-element="SettingsDrawer" data-sentry-source-file="side-nav.tsx" />
							<Chip label={[process.env.VERSION, process.env.ENV].filter(Boolean).join("-")} variant="filled" color="primary" sx={{
              m: 0
            }} data-sentry-element="Chip" data-sentry-source-file="side-nav.tsx" />
						</Stack>
					</Stack>
				</Scrollbar>
			</SwipeableDrawer>
		</>;
};